import { bindable, BindingMode, customElement } from 'aurelia';
import template from './localized-date-time-input.html';

@customElement({ name: 'localized-date-time-input', template })
export class LocalizedDateTimeInput {
  @bindable({ mode: BindingMode.twoWay }) value = '';
  @bindable locale = 'en-US';
  @bindable disabled = false;
  @bindable label = '';
  @bindable timeLabel = 'Time';
  @bindable okLabel = 'OK';
  @bindable cancelLabel = 'Cancel';
  @bindable changeCallback?: (value: string) => void;

  public displayValue = '';
  public pickerOpen = false;
  public pickerDate = '';
  public pickerTime = '';

  public binding(): void {
    this.syncDisplayValue();
  }

  public valueChanged(): void {
    this.syncDisplayValue();
  }

  public localeChanged(): void {
    this.syncDisplayValue();
  }

  public get placeholder(): string {
    return this.isGerman ? 'DD.MM.YYYY HH:mm:ss' : 'MM/DD/YYYY HH:mm:ss';
  }

  public get nativeLanguage(): 'de' | 'en-US' {
    return this.isGerman ? 'de' : 'en-US';
  }

  public onTextInput(event: Event): void {
    this.displayValue = (event.target as HTMLInputElement).value;
  }

  public onTextKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.commitTextValue();
  }

  public onTextBlur(): void {
    this.commitTextValue();
  }

  public openPicker(event: MouseEvent): void {
    event.preventDefault();
    this.commitTextValue();
    const date = this.parseStoredValue(this.value) ?? new Date();
    this.pickerDate = this.toNativeDateValue(date);
    this.pickerTime = this.toNativeTimeValue(date);
    this.pickerOpen = true;
  }

  public cancelPicker(): void {
    this.pickerOpen = false;
  }

  public confirmPicker(): void {
    const parsed = this.parseNativeValues(this.pickerDate, this.pickerTime);
    if (!parsed) return;

    this.setValue(parsed.toISOString());
    this.pickerOpen = false;
  }

  public onPickerKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelPicker();
    }
  }

  private get isGerman(): boolean {
    return this.locale.toLowerCase().startsWith('de');
  }

  private syncDisplayValue(): void {
    this.displayValue = this.formatValue(this.value);
  }

  private commitTextValue(): void {
    const text = this.displayValue.trim();
    if (!text) {
      this.setValue('');
      return;
    }

    const parsed = this.parseTextValue(text);
    if (!parsed) {
      this.syncDisplayValue();
      return;
    }

    this.setValue(parsed.toISOString());
  }

  private formatValue(value: string): string {
    const date = this.parseStoredValue(value);
    if (!date) return value;

    const separator = this.isGerman ? '.' : '/';
    const first = this.isGerman ? date.getDate() : date.getMonth() + 1;
    const second = this.isGerman ? date.getMonth() + 1 : date.getDate();
    return `${this.pad(first)}${separator}${this.pad(second)}${separator}${date.getFullYear()} `
      + `${this.pad(date.getHours())}:${this.pad(date.getMinutes())}:${this.pad(date.getSeconds())}`;
  }

  private parseStoredValue(value: string): Date | null {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private parseTextValue(value: string): Date | null {
    const match = /^(\d{1,2})[./](\d{1,2})[./](\d{4})(?:[ T]+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/.exec(value);
    if (!match) return null;

    const first = Number(match[1]);
    const second = Number(match[2]);
    return this.createLocalDate(
      Number(match[3]),
      this.isGerman ? second : first,
      this.isGerman ? first : second,
      Number(match[4] ?? 0),
      Number(match[5] ?? 0),
      Number(match[6] ?? 0),
    );
  }

  private parseNativeValues(dateValue: string, timeValue: string): Date | null {
    const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue);
    const timeMatch = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(timeValue);
    if (!dateMatch || !timeMatch) return null;

    return this.createLocalDate(
      Number(dateMatch[1]),
      Number(dateMatch[2]),
      Number(dateMatch[3]),
      Number(timeMatch[1]),
      Number(timeMatch[2]),
      Number(timeMatch[3] ?? 0),
    );
  }

  private createLocalDate(
    year: number,
    month: number,
    day: number,
    hour: number,
    minute: number,
    second: number,
  ): Date | null {
    const date = new Date(year, month - 1, day, hour, minute, second, 0);
    if (
      date.getFullYear() !== year
      || date.getMonth() !== month - 1
      || date.getDate() !== day
      || date.getHours() !== hour
      || date.getMinutes() !== minute
      || date.getSeconds() !== second
    ) {
      return null;
    }

    return date;
  }

  private toNativeDateValue(date: Date): string {
    return `${date.getFullYear()}-${this.pad(date.getMonth() + 1)}-${this.pad(date.getDate())}`;
  }

  private toNativeTimeValue(date: Date): string {
    return `${this.pad(date.getHours())}:${this.pad(date.getMinutes())}:${this.pad(date.getSeconds())}`;
  }

  private setValue(value: string): void {
    this.value = value;
    this.changeCallback?.(value);
  }

  private pad(value: number): string {
    return String(value).padStart(2, '0');
  }
}
